/* eslint-disable max-lines */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  useMemo
} from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { styled } from '@this/constants/themes';
import _ from 'lodash';

import { Fetcher, HTTPError, appendScript } from '@this/src/util';
import { buildBakurakuWorkflowRequestFromSnapshot } from '@this/domain/bakuraku_workflow_request';
import { reportError } from '@this/lib/bugsnag';
import { observer } from 'mobx-react';
import type { ReserveConfirmStoreResponseArgs } from '@this/src/domain/reserve_confirm_store';
import ReserveConfirmStore, {
  convertReserveConfirmStoreResponseToArgs
} from '@this/src/domain/reserve_confirm_store';
import type Service from '@this/domain/service';
import Transit from '@this/domain/transit/transit2';
import type {
  PollInventoryCheckArgs,
  InventoryResultArgs
} from '@this/src/api/domestic_air/same_day_inventory_checks_api';
import {
  startSameDayInventoryCheck,
  fetchSameDayInventoryCheck,
  confirmSameDayInventoryCheck
} from '@this/src/api/domestic_air/same_day_inventory_checks_api';
import { worstInventoryStatus } from '@this/domain/domestic_air/same_day_inventory_status';
import ReserveConfirmPcTemplate from './reserve_confirm_pc.template';
import ReserveConfirmSpTemplate from './reserve_confirm_sp.template';
import type {
  SameDayInventoryPhase,
  SameDayFlightInfoArgs,
  SameDayOriginalTicketArgs
} from './same_day_inventory_check_modal';
import SameDayInventoryCheckModal from './same_day_inventory_check_modal';

// 在庫確認ポーリング設定
const INVENTORY_POLL_INTERVAL_MS = 5000;
const INVENTORY_POLL_TIMEOUT_MS = 120000;
const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

// 在庫確認結果を5秒間隔でポーリングし、completed/stale になったら返す。
// フロント側タイムアウト（120秒）超過時は例外を投げ、呼び出し側で共通エラー扱いにする。
const pollInventoryCheck = async (checkId: string, searchQueryId: number): Promise<PollInventoryCheckArgs> => {
  const startedAt = Date.now();
  for (;;) {
    const res = await fetchSameDayInventoryCheck(checkId, searchQueryId);
    if (res.status !== 'in_progress') return res;
    if (Date.now() - startedAt > INVENTORY_POLL_TIMEOUT_MS) {
      throw new Error('same day inventory check timeout');
    }
    await sleep(INVENTORY_POLL_INTERVAL_MS);
  }
};

export const steps = ['Step 1. 入力', 'Step 2. 確認', 'Step 3. 完了'];

interface PaymentResponse {
  firstTrip: boolean;
  trip_id: never;
}

interface GmoTokenResult {
  tokenObject: {
    token: string;
    toBeExpiredAt: string;
    maskedCardNo: string;
    isSecurityCodeSet: boolean;
  };
  resultCode: string;
}

interface FreeeAuthUriResponse {
  freee_auth_uri: string | null;
}

const getInitialPurpose = (result: ReserveConfirmStoreResponseArgs) => result.draft_trip?.purpose || '';

const getInitialBakurakuWorkflowRequest = (result: ReserveConfirmStoreResponseArgs) =>
  buildBakurakuWorkflowRequestFromSnapshot(
    result.draft_trip
      ? {
          bakurakuWorkflowRequestId: result.draft_trip.bakuraku_workflow_request_id,
          bakurakuWorkflowRequestTitle: result.draft_trip.bakuraku_workflow_request_title,
          bakurakuWorkflowRequestNum: result.draft_trip.bakuraku_workflow_request_num
        }
      : null
  );

interface Props extends RouteComponentProps {
  service: Service;
}

export interface ReserveConfirmHandle {
  handlePaymentSubmit: () => void;
}

const extractResponseErrors = (error: unknown): string[] => {
  if (!(error instanceof HTTPError)) return [];

  const errors = error.response?.data?.errors;
  if (typeof errors === 'string') return [errors];
  if (Array.isArray(errors)) {
    return errors.filter((message): message is string => typeof message === 'string');
  }

  return [];
};

const formatResponseErrors = (errors: string[], fallback: string): string => {
  return errors.length > 0 ? errors.join('\n') : fallback;
};

const ReserveConfirm = React.forwardRef<ReserveConfirmHandle, Props>(({ service, history }, ref) => {
  const tokenRef = useRef<HTMLInputElement>(null);

  const [store, setStore] = useState<ReserveConfirmStore | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [returning, setReturning] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [itemsBlankError, setItemsBlankError] = useState<boolean>(false);
  const [responseError, setResponseError] = useState<string | undefined>(undefined);

  // 当日予約: 在庫確認モーダルの状態
  const [inventoryModalOpen, setInventoryModalOpen] = useState<boolean>(false);
  const [inventoryPhase, setInventoryPhase] = useState<SameDayInventoryPhase>('checking');
  const [inventoryResults, setInventoryResults] = useState<InventoryResultArgs[]>([]);
  const [inventoryError, setInventoryError] = useState<string>('');
  const [inventorySubmitting, setInventorySubmitting] = useState<boolean>(false);
  const inventoryCheckIdRef = useRef<string | null>(null);

  const handleToken = useCallback((result: GmoTokenResult) => {
    if (tokenRef.current) {
      $(tokenRef.current).trigger('onChange', result);
    }
  }, []);

  const handlePaymentSucceed = useCallback((result: PaymentResponse) => {
    const queryFirst = result.firstTrip ? '?first=true' : '';
    const url = `/trips/${result.trip_id}${queryFirst}`;
    location.href = url;
  }, []);

  const handleTokenChange = useCallback(
    (e: unknown, result: GmoTokenResult) => {
      if (result.resultCode === '000') {
        if (!store) return;
        store.paymentFailed = false;
        const params = _.merge(store?.reserveInfo.submitParams, {
          token: result.tokenObject.token
        });
        Fetcher.post<PaymentResponse>('/payments/token.json', params).then(
          paymentResult => {
            handlePaymentSucceed(paymentResult);
          },
          error => {
            const extractedErrors = extractResponseErrors(error);
            const errorMessages = formatResponseErrors(
              extractedErrors,
              '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
            );
            if (!store) {
              return;
            }
            store.paymentLoading = false;
            store.paymentFailed = true;
            store.paymentErrors = errorMessages;
          }
        );
      } else if (result.resultCode === '901') {
        if (!store) {
          return;
        }
        store.paymentLoading = false;
        store.paymentFailed = true;
        store.paymentErrors = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
      } else {
        if (!store) {
          return;
        }
        store.paymentLoading = false;
        store.paymentFailed = true;
        store.paymentErrors = '決済に失敗しました。\n入力内容に誤りがないかご確認ください。';
        store.paymentFailed = false;
      }
    },
    [store?.reserveInfo, handlePaymentSucceed]
  );

  const facebookAdConversion = useCallback(() => {
    const totalPrice = store?.reserveInfo?.reservingTrip.totalPrice;
    if (totalPrice != null) {
      fbq('track', 'Purchase', {
        value: totalPrice,
        currency: 'JPY'
      });
    }
  }, [store?.reserveInfo]);

  const requestCardSeq = useCallback(() => {
    if (!store?.reserveInfo) return;

    const params = _.merge(store.reserveInfo.submitParams, {
      payment_transaction: store.reserveInfo.paymentTransaction
    });

    Fetcher.post<PaymentResponse>('/payments/card_seq.json', params).then(
      result => {
        facebookAdConversion();
        handlePaymentSucceed(result);
      },
      error => {
        const extractedErrors = extractResponseErrors(error);
        const errorMessages = formatResponseErrors(extractedErrors, approvalErrorMessage());
        store.paymentLoading = false;
        store.paymentFailed = true;
        store.paymentErrors = errorMessages;
      }
    );
  }, [store, facebookAdConversion, handlePaymentSucceed]);

  const setSonyPaymentTokenCallback = useCallback(
    (token: string, _maskedCard: string) => {
      if (store?.reserveInfo) {
        store.reserveInfo.paymentTransaction = { token, uid: 'new' };
        requestCardSeq();
      }
    },
    [store?.reserveInfo, requestCardSeq]
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (store) {
      return;
    }

    const url = new URL(location.href);
    const searchQueryId = url.searchParams.get('search_query_id') ?? '';

    if (location.hash && location.hash.length > 0) {
      location.href = `/reserve_confirm?search_query_id=${searchQueryId}`;
      return;
    }
    Fetcher.get<ReserveConfirmStoreResponseArgs>(
      `/reserve_confirm/show.json?search_query_id=${searchQueryId}&${Date.now()}`
    )
      .then(async result => {
        const s = new ReserveConfirmStore(
          convertReserveConfirmStoreResponseToArgs(result, { isSimpleRequest: false, fromTripForm: false })
        );
        s.reserveInfo.purpose = getInitialPurpose(result);
        s.reserveInfo.setBakurakuWorkflowRequest(getInitialBakurakuWorkflowRequest(result));
        if (s.reserveInfo.user.organization?.freeeWorkflow) {
          const freeeAuthUriResponse = await Fetcher.get<FreeeAuthUriResponse>(
            '/organization/freee/auth_uri.json'
          );
          s.reserveInfo.user.freeeAuthUri = freeeAuthUriResponse.freee_auth_uri ?? undefined;
        }
        await s.reserveInfo.fetchApproveItems('before_business_trip');
        setStore(s);
        if (s.reserveInfo.paymentGatewayType === 'gmo') {
          appendScript(`https://${s.tokenHost}/ext/js/token.js`);
          if (tokenRef.current) {
            $(tokenRef.current).bind('onChange', handleTokenChange);
          }
        }

        if (s.reserveInfo.paymentGatewayType === 'sony') {
          const scriptUrl = s.sonyPaymentGatewayTokenGenerateScriptUrl;
          if (scriptUrl) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = scriptUrl;
            script.setAttribute('callBackFunc', 'setSonyPaymentToken');
            script.className = 'spsvToken';
            document.head.appendChild(script);

            window.setSonyPaymentToken = setSonyPaymentTokenCallback as (
              token: string,
              maskedCard: string
            ) => void;
          }
        }

        setLoading(false);
      })
      .then(
        () => {},
        error => {
          if (error instanceof HTTPError && error.response?.status === 400) {
            const errors = extractResponseErrors(error);
            const message = errors.join('\n');
            if (message) {
              if (errors.includes('items_blank')) {
                setItemsBlankError(true);
                setLoading(false);
              } else {
                setShowError(true);
                setLoading(false);
                setResponseError(message);
              }
            } else {
              setReturning(true);
              setLoading(false);
            }
          } else {
            const errors = extractResponseErrors(error);
            setShowError(true);
            setLoading(false);
            setResponseError(errors[0]);
          }
        }
      );
  }, [store, handleTokenChange, setSonyPaymentTokenCallback]);

  useEffect(() => {
    return () => {
      if (store?.reserveInfo?.paymentGatewayType === 'sony') {
        window.setSonyPaymentToken = null;
      }
    };
  }, [store?.reserveInfo?.paymentGatewayType]);

  // openSelectTravelers(index: never, e: Event) {
  //   e.preventDefault();
  //   this.setState({
  //     selectedIndex: index,
  //     showSelectTravelers: true
  //   });
  // }

  // closeSelectTravelers(e: Event) {
  //   e.preventDefault();
  //   this.setState({ showSelectTravelers: false });
  // }

  const handleBackToSelectClick = useCallback(() => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      history.push(store?.selectPageUrl || '/dashboard');
    }, 100);
  }, [history, store?.selectPageUrl]);

  // 実決済送信（既存ロジック: 請求払い / GMOトークン / Sonyトークン分岐）。
  // 在庫確認ゲート通過後に呼び出す。
  const submitPayment = useCallback(() => {
    if (!store) return;
    store.paymentLoading = true;

    if (!store?.reserveInfo) return;
    if (!store.reserveInfo.canPayment) {
      store.paymentLoading = false;
      store.paymentFailed = true;
      store.paymentErrors = 'こちらの旅程は手配依頼できません。';
      reportError(new Error('こちらの旅程は手配依頼できません。'));
    }
    if (store.reserveInfo.paymentMethodType === 'card' && store.reserveInfo.paymentGatewayType === 'gmo') {
      if (tokenRef.current) {
        $(tokenRef.current).unbind('onChange');
        $(tokenRef.current).bind('onChange', handleTokenChange);
      }

      if (store.reserveInfo.paymentTransaction?.uid === 'new') {
        if (typeof Multipayment !== 'undefined') {
          Multipayment.init(store.shopId);
          Multipayment.getToken(store.reserveInfo.newCardParams, handleToken);
        } else {
          store.paymentLoading = false;
          store.paymentFailed = true;
          store.paymentErrors = approvalErrorMessage();
          reportError(new Error('Multipayment does not exist'));
        }

        return;
      }
    }

    if (store.reserveInfo.paymentMethodType === 'card' && store.reserveInfo.paymentGatewayType === 'sony') {
      if (store.reserveInfo.paymentTransaction?.uid === 'new') {
        const cardParams = store.reserveInfo.newCardParams;
        SpsvApi.spsvCreateToken(
          cardParams.cardno,
          cardParams.expireYear,
          cardParams.expireMonth,
          cardParams.securitycode,
          '',
          '',
          '',
          '',
          ''
        );
      } else {
        requestCardSeq();
        return;
      }
    }

    if (store.reserveInfo.paymentMethod === 'billing') {
      Fetcher.post<PaymentResponse>('/payments/billing.json', store.reserveInfo.submitParams).then(
        result => {
          facebookAdConversion();
          handlePaymentSucceed(result);
        },
        error => {
          const extractedErrors = extractResponseErrors(error);
          const errorMessages = formatResponseErrors(extractedErrors, approvalErrorMessage());
          store.paymentLoading = false;
          store.paymentFailed = true;
          store.paymentErrors = errorMessages;
        }
      );
      return;
    }
    if (store.reserveInfo.paymentMethodType === 'card' && store.reserveInfo.paymentGatewayType === 'gmo') {
      requestCardSeq();
    }
  }, [store, handleTokenChange, handleToken, requestCardSeq, facebookAdConversion, handlePaymentSucceed]);

  // 「検索結果に戻る」: 在庫確認モーダルを閉じ、既存の検索結果導線と同じ遷移先へ
  const handleInventoryBackToSelect = useCallback(() => {
    setInventoryModalOpen(false);
    if (store) store.paymentLoading = false;
    handleBackToSelectClick();
  }, [store, handleBackToSelectClick]);

  // 券種変更（ticket_type_changed）の確定 → storeのindexを更新して実決済送信
  const handleConfirmTicketChange = useCallback(async () => {
    const checkId = inventoryCheckIdRef.current;
    if (!store?.reserveInfo || !checkId) return;
    const searchQueryId = store.reserveInfo.searchQueryId;
    if (searchQueryId == null) return;
    setInventorySubmitting(true);
    try {
      const res = await confirmSameDayInventoryCheck(checkId, { search_query_id: searchQueryId });
      if (res.status === 'confirmed') {
        // 差し替え後のprice_indexをstoreのReservingTripItemへ反映（決済payloadに反映される）
        res.results.forEach(result => {
          const item = store.reserveInfo.reservingTrip.items.find(i => Number(i.index) === result.itemIndex);
          if (item) item.domesticAirPriceIndex = result.priceIndex;
        });
        setInventorySubmitting(false);
        setInventoryModalOpen(false);
        submitPayment();
        return;
      }
      // stale / candidate_not_found
      setInventorySubmitting(false);
      store.paymentLoading = false;
      setInventoryPhase('error');
      setInventoryError(
        res.status === 'stale'
          ? '検索結果の有効期限が切れました。お手数ですが検索結果に戻ってやり直してください。'
          : '選択した券種の空席が確保できませんでした。お手数ですが検索結果に戻ってやり直してください。'
      );
    } catch (e) {
      setInventorySubmitting(false);
      store.paymentLoading = false;
      setInventoryModalOpen(false);
      store.paymentFailed = true;
      store.paymentErrors = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
    }
  }, [store, submitPayment]);

  // モーダルの「手配を依頼」ボタン
  const handleInventoryProceed = useCallback(() => {
    if (inventoryPhase === 'ticket_type_changed') {
      void handleConfirmTicketChange();
      return;
    }
    // price_increased: そのまま実決済送信
    setInventoryModalOpen(false);
    submitPayment();
  }, [inventoryPhase, handleConfirmTicketChange, submitPayment]);

  // 在庫確認ゲート: 開始API → ポーリング → 結果分岐
  const runInventoryGate = useCallback(
    async (searchQueryId: number) => {
      if (!store?.reserveInfo) return;
      store.paymentFailed = false;
      store.paymentLoading = true;
      setInventoryError('');
      setInventoryResults([]);
      inventoryCheckIdRef.current = null;
      setInventoryPhase('checking');
      setInventoryModalOpen(true);
      try {
        const start = await startSameDayInventoryCheck({ search_query_id: searchQueryId });
        if (!start.target || !start.checkId) {
          // 在庫確認対象なし → モーダルを閉じてそのまま実決済送信
          setInventoryModalOpen(false);
          submitPayment();
          return;
        }
        inventoryCheckIdRef.current = start.checkId;
        const polled = await pollInventoryCheck(start.checkId, searchQueryId);
        if (polled.status === 'completed') {
          inventoryCheckIdRef.current = polled.checkId;
          setInventoryResults(polled.results);
          const worst = worstInventoryStatus(polled.results);
          if (worst === 'available' || worst === 'price_decreased' || worst === 'operator_check') {
            setInventoryModalOpen(false);
            submitPayment();
            return;
          }
          store.paymentLoading = false;
          if (worst === 'price_increased') {
            setInventoryPhase('price_increased');
          } else if (worst === 'ticket_type_changed') {
            setInventoryPhase('ticket_type_changed');
          } else {
            setInventoryPhase('unavailable');
          }
          return;
        }
        // stale もしくは想定外（in_progress）→ 検索結果に戻す
        store.paymentLoading = false;
        setInventoryPhase('error');
        setInventoryError('検索結果の有効期限が切れました。お手数ですが検索結果に戻ってやり直してください。');
      } catch (e) {
        // タイムアウト・API通信エラー → 共通エラー扱い
        store.paymentLoading = false;
        setInventoryModalOpen(false);
        store.paymentFailed = true;
        store.paymentErrors = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
      }
    },
    [store, submitPayment]
  );

  // 手配依頼押下時のエントリポイント（在庫確認ゲート）
  const handlePaymentSubmit = useCallback(() => {
    if (!store?.reserveInfo) return;
    // 二重送信防止（既存 paymentLoading + モーダル表示中は弾く）
    if (store.paymentLoading || inventoryModalOpen) return;

    const searchQueryId = store.reserveInfo.searchQueryId;
    const hasDomesticAir = store.reserveInfo.reservingTrip.items.some(
      item => item.isTransit && item.airExists && item.isDomesticExist
    );
    // 国内航空券を含まない、または search_query_id 不明なら在庫確認せず従来通り
    if (!hasDomesticAir || searchQueryId == null) {
      submitPayment();
      return;
    }
    void runInventoryGate(searchQueryId);
  }, [store, inventoryModalOpen, submitPayment, runInventoryGate]);

  const { flightInfos, originalTickets } = useMemo<{
    flightInfos: Record<number, SameDayFlightInfoArgs>;
    originalTickets: Record<number, SameDayOriginalTicketArgs>;
  }>(() => {
    const flights: Record<number, SameDayFlightInfoArgs> = {};
    const tickets: Record<number, SameDayOriginalTicketArgs> = {};
    const items = store?.reserveInfo?.reservingTrip.items ?? [];
    items.forEach(item => {
      if (!(item.element instanceof Transit)) return;
      const idx = item.domesticAirPriceIndex;
      if (idx == null || item.index == null) return;
      const itemIndex = Number(item.index);

      const price = item.element.prices?.[idx];
      if (price) {
        tickets[itemIndex] = { seatType: price.seatType, ticketType: price.ticketType ?? undefined };
      }

      // 空港アクセス区間（電車等）を除いた航空セグメント基準で便情報を組み立てる
      const airSegments = (item.element.segments ?? []).filter(segment => segment.isAir);
      const firstSegment = airSegments[0];
      const lastSegment = airSegments[airSegments.length - 1];
      if (!firstSegment || firstSegment.legs.length === 0 || lastSegment.legs.length === 0) return;
      const firstLeg = firstSegment.legs[0];
      const lastLeg = lastSegment.legs[lastSegment.legs.length - 1];
      flights[itemIndex] = {
        route: `${firstLeg.from.name} → ${lastLeg.to.name}`,
        departureDate: firstLeg.from.date ?? '',
        timeRange: `${firstLeg.from.time}発 → ${lastLeg.to.time}着`,
        flightNumber: firstLeg.name
      };
    });
    return { flightInfos: flights, originalTickets: tickets };
  }, [store]);

  useImperativeHandle(
    ref,
    () => ({
      handlePaymentSubmit
    }),
    [handlePaymentSubmit]
  );

  const approvalErrorMessage = () => {
    return `申し訳ございません。ご予約を受け付けることができませんでした。

時間を空けてもう一度お試しいただき、改善されない場合は
[お問い合わせフォーム](/inquiries/new)よりご連絡ください。`;
  };

  try {
    return (
      <Confirm>
        {/* onChangeは第2引数とらないのでhandleTokenChangeの型が使えない */}
        <input id="token" type="hidden" onChange={handleTokenChange as any} ref={tokenRef} />
        <ConfirmPc>
          <ReserveConfirmPcTemplate
            service={service}
            store={store}
            loading={loading}
            responseError={responseError}
            returning={returning}
            itemsBlankError={itemsBlankError}
            showError={showError}
            handleBackToSelectClick={handleBackToSelectClick}
            handlePaymentSubmit={handlePaymentSubmit}
          />
        </ConfirmPc>
        <ConfirmSp>
          <ReserveConfirmSpTemplate
            service={service}
            store={store}
            loading={loading}
            responseError={responseError}
            returning={returning}
            itemsBlankError={itemsBlankError}
            showError={showError}
            handleBackToSelectClick={handleBackToSelectClick}
            handlePaymentSubmit={handlePaymentSubmit}
          />
        </ConfirmSp>
        {inventoryModalOpen && (
          <SameDayInventoryCheckModal
            open={inventoryModalOpen}
            phase={inventoryPhase}
            results={inventoryResults}
            flightInfos={flightInfos}
            originalTickets={originalTickets}
            errorMessage={inventoryError}
            submitting={inventorySubmitting}
            onProceed={handleInventoryProceed}
            onBackToSelect={handleInventoryBackToSelect}
          />
        )}
      </Confirm>
    );
  } catch (e) {
    reportError(e as Error);
    return null;
  }
});

const baseColor = '#af985e';
const blackColor = '#292929';
const grayColor = '#eee';

const Confirm = styled.div`
  min-height: 100vh;
`;

const ConfirmPc = styled.div`
  display: block;

  min-height: 100vh;

  @media (max-width: 1000px) {
    display: none;
  }

  > :first-child {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin: 0 auto;
  }

  header {
    background: ${blackColor};
    border-bottom: 1px solid ${baseColor};
  }

  .header-in {
    max-width: 100%;
    background: none;
    border-bottom: none;
    padding: 0 20px;
  }

  .logo {
    padding-left: 0;
  }

  .trips-button {
    margin-right: 0;
  }

  .in-advance-button {
    margin-right: 10px;
  }

  .info-content {
    position: relative;
    background: white;

    &__title {
      &__link {
        position: absolute;
        top: 10px;
        right: 10px;
        margin: 0;
      }
    }
  }
`;

const ConfirmSp = styled.div`
  display: none;

  min-height: 100vh;

  @media (max-width: 1000px) {
    display: block;
  }

  > :first-child {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin: 0 auto;
  }

  .header-in {
    max-width: 100%;
  }

  .content-wrapper {
    flex-grow: 9999;
    display: flex;
    flex-direction: column;
    width: 100%;
    background: ${grayColor};
  }

  .content-body {
    max-width: 100%;
    padding: 10px;
  }
`;

export default observer(ReserveConfirm);
